import { useCallback } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createPushSubscription,
  destroyPushSubscription,
  indexPushSubscriptions,
  pushSubscriptionQueryKeys,
  PushSubscriptionRow,
  sendTestPushNotification,
} from 'services/api/pushSubscriptions';

const NO_DEVICES: PushSubscriptionRow[] = [];
export const OUTCOME_REFETCH_DELAY_MS = 4000;

/**
 * The API's side of push for the signed-in user (OLIO-3563): every device they have registered, the
 * server's VAPID public key, and the three writes — register a device, remove one, send a test.
 * Each write invalidates the list so the devices and "this device" refresh from the server.
 */
export function usePushDevices({ active = true }: { active?: boolean } = {}) {
  const queryClient = useQueryClient();

  const index = useQuery({
    queryKey: pushSubscriptionQueryKeys.index(),
    queryFn: indexPushSubscriptions,
    enabled: active,
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: pushSubscriptionQueryKeys.index() }),
    [queryClient]
  );

  const register = useMutation({ mutationFn: createPushSubscription, onSuccess: invalidate });
  const remove = useMutation({ mutationFn: destroyPushSubscription, onSuccess: invalidate });
  // The test send only queues; each device's outcome lands on its row a moment later, so refetch
  // the list again once the jobs have had a chance to run.
  const sendTest = useMutation({
    mutationFn: sendTestPushNotification,
    onSuccess: () => {
      invalidate();
      setTimeout(invalidate, OUTCOME_REFETCH_DELAY_MS);
    },
  });

  return {
    devices: index.data?.data ?? NO_DEVICES,
    vapidPublicKey: index.data?.meta.vapidPublicKey ?? null,
    isPending: index.isPending,
    isSuccess: index.isSuccess,
    // The index failing (403 when the flag is off server-side, 500, offline) is not "no keys".
    error: index.error,
    refetch: index.refetch,
    register,
    remove,
    sendTest,
  };
}
